/**
 * @fileoverview Generador de variantes de coordenadas con dígito extra
 * @module lib/coordinate/coordinateVariants
 *
 * F2.06a — Variantes candidatas para dígito extra en coordenadas PTEL
 *
 * Cuando una coordenada tiene exactamente un dígito de más en su parte
 * entera (rawDigits.length === expectedLength + 1), genera todas las
 * eliminaciones posibles y las ordena por proximidad numérica al valor
 * de referencia (primeros N dígitos del original interpretados como entero).
 *
 * Esta función es PURA: sin I/O, sin dependencias externas.
 * La selección final de la mejor candidata corresponde a F2.06b
 * (rankCandidatesByCoherence), que usará validación espacial.
 *
 * @version 1.0.0
 * @date 2026-03-19
 */

// ============================================================================
// TIPOS
// ============================================================================

/**
 * Candidata generada por eliminación de un dígito extra.
 */
export interface CoordinateCandidate {
  /** Valor numérico resultante (parte entera + decimal si procede) */
  value: number;
  /** Índice del dígito eliminado en rawDigits (0-based) */
  removedDigitIndex: number;
  /** Carácter del dígito eliminado */
  removedDigit: string;
  /** Cadena de dígitos enteros original (sin separadores, sin decimal) */
  source: string;
}

// ============================================================================
// FUNCIÓN PRINCIPAL
// ============================================================================

/**
 * Genera todas las variantes posibles eliminando exactamente un dígito extra
 * de la parte entera de una coordenada con longitud inesperada.
 *
 * Solo actúa cuando `rawDigits.length === expectedLength + 1`.
 * Devuelve `[]` en cualquier otro caso (sin exceso o exceso > 1).
 *
 * Filtro: se descarta cualquier resultado con cero inicial («0XXXXXX»),
 * ya que ninguna coordenada UTM/ETRS89 válida empieza por 0.
 *
 * Las candidatas se ordenan por proximidad numérica al valor de referencia:
 * `parseInt(rawDigits.slice(0, expectedLength), 10)`.
 *
 * @param rawDigits      - Dígitos de la parte entera sin separadores (p. ej. «40783789»)
 * @param expectedLength - Longitud esperada: 6 para X UTM30, 7 para Y UTM30
 * @param decimals       - Parte decimal sin punto (p. ej. «07»). Cadena vacía si no hay decimales.
 * @returns Array de candidatas ordenadas por proximidad al valor de referencia
 *
 * @example
 * // Caso Berja #148: Y con 8 dígitos, esperados 7
 * generateDigitRemovalCandidates('40783789', 7, '07')
 * // Resultado[0]: { value: 4078378.07, removedDigitIndex: 7, removedDigit: '9', source: '40783789' }
 * // Resultado[1]: { value: 4078379.07, removedDigitIndex: 6, removedDigit: '8', ... }
 *
 * @example
 * // X con 7 dígitos, esperados 6, sin decimales
 * generateDigitRemovalCandidates('4537821', 6, '')
 * // Resultado[0]: { value: 453782, removedDigitIndex: 6, removedDigit: '1', ... }
 */
export function generateDigitRemovalCandidates(
  rawDigits: string,
  expectedLength: number,
  decimals: string,
): CoordinateCandidate[] {
  // Precondición: exactamente 1 dígito extra
  if (rawDigits.length !== expectedLength + 1) {
    return [];
  }

  // Valor de referencia para ordenar: primeros N dígitos del original
  const referenceValue = parseInt(rawDigits.slice(0, expectedLength), 10);

  // Sufijo decimal para construir el número final
  const decimalSuffix = decimals.length > 0 ? `.${decimals}` : '';

  const candidates: CoordinateCandidate[] = [];

  for (let i = 0; i < rawDigits.length; i++) {
    const reducedDigits = rawDigits.slice(0, i) + rawDigits.slice(i + 1);

    // Descartar candidatas con cero inicial (sin coordenada UTM válida empieza por 0)
    if (reducedDigits.startsWith('0')) continue;

    const value = parseFloat(reducedDigits + decimalSuffix);

    candidates.push({
      value,
      removedDigitIndex: i,
      removedDigit: rawDigits[i],
      source: rawDigits,
    });
  }

  // Ordenar por proximidad al valor de referencia (distancia ascendente)
  candidates.sort(
    (a, b) => Math.abs(a.value - referenceValue) - Math.abs(b.value - referenceValue),
  );

  return candidates;
}

// ============================================================================
// TIPOS F2.06b
// ============================================================================

/**
 * Candidata enriquecida con puntuación heurística.
 * Extiende CoordinateCandidate añadiendo score y reason.
 */
export interface RankedCandidate extends CoordinateCandidate {
  /** Puntuación heurística total (0–100) */
  score: number;
  /** Descripción legible de los componentes del score (p. ej. «pos0:+40, rango-utm:+30») */
  reason: string;
}

// ============================================================================
// CONSTANTES DE RANGOS UTM30 ANDALUCÍA
// ============================================================================

interface AxisRange {
  /** Límite inferior del rango UTM válido para Andalucía */
  min: number;
  /** Límite superior del rango UTM válido para Andalucía */
  max: number;
  /**
   * Rango «ajustado»: área mayoritaria de Andalucía, excluyendo extremos marginales.
   * Aporta +20 pts adicionales por proximidad al área de uso habitual.
   */
  tightMin: number;
  tightMax: number;
}

const UTM_RANGE: Record<'X' | 'Y', AxisRange> = {
  X: { min: 100_000,   max: 900_000,   tightMin: 100_000,   tightMax: 600_000   },
  Y: { min: 3_900_000, max: 4_400_000, tightMin: 3_950_000, tightMax: 4_300_000 },
};

/**
 * Puntuaciones base por posición en la lista ordenada de F2.06a.
 * Posición 0 (más próxima numéricamente al valor de referencia) → +40 pts.
 * Posiciones ≥ 3 comparten el mínimo de +10 pts.
 */
const BASE_SCORE_BY_POSITION = [40, 30, 20, 10] as const;

// ============================================================================
// F2.06b — FUNCIÓN DE RANKING
// ============================================================================

/**
 * Ordena candidatas de F2.06a por coherencia heurística con el espacio UTM30
 * de Andalucía. Función pura: sin I/O ni dependencias externas.
 *
 * ### Criterios de puntuación
 *
 * | Puntos  | Criterio |
 * |---------|----------|
 * | +40/30/20/10 | Base por posición en lista F2.06a (proximidad numérica al valor de referencia) |
 * | +30     | Valor dentro del rango UTM30 válido para Andalucía (`UTM_RANGE[axis]`) |
 * | +20     | Valor dentro del rango «ajustado» típico de Andalucía (excluye extremos marginales) |
 * | +10     | Patrón tipográfico «tecla pegada»: dígito eliminado == dígito adyacente en la fuente |
 *
 * Los empates en score conservan el orden F2.06a (sort estable).
 *
 * @param candidates - Lista de candidatas de F2.06a (ordenadas por proximidad numérica)
 * @param axis       - Eje UTM: `'X'` (easting) o `'Y'` (northing)
 * @returns Candidatas enriquecidas con `score` y `reason`, ordenadas por score descendente
 *
 * @example
 * // Caso Berja #148: Y con 8 dígitos, esperados 7
 * const candidates = generateDigitRemovalCandidates('40783789', 7, '07');
 * const ranked = rankCandidatesByCoherence(candidates, 'Y');
 * // ranked[0].value ≈ 4078378.07  (coordenada correcta)
 * // ranked[0].score > 70
 */
export function rankCandidatesByCoherence(
  candidates: CoordinateCandidate[],
  axis: 'X' | 'Y',
): RankedCandidate[] {
  if (candidates.length === 0) return [];

  const range = UTM_RANGE[axis];

  const ranked: RankedCandidate[] = candidates.map((candidate, position) => {
    const parts: string[] = [];
    let score = 0;

    // ── 1. Base por posición F2.06a (proximidad numérica al valor de referencia) ──
    const posScore =
      BASE_SCORE_BY_POSITION[Math.min(position, BASE_SCORE_BY_POSITION.length - 1)];
    score += posScore;
    parts.push(`pos${position}:+${posScore}`);

    // ── 2. ¿Valor dentro del rango UTM30 válido para Andalucía? ──────────────────
    if (candidate.value >= range.min && candidate.value <= range.max) {
      score += 30;
      parts.push('rango-utm:+30');
    }

    // ── 3. ¿Valor dentro del rango «ajustado» (área mayoritaria de Andalucía)? ───
    if (candidate.value >= range.tightMin && candidate.value <= range.tightMax) {
      score += 20;
      parts.push('rango-ajustado:+20');
    }

    // ── 4. Patrón tipográfico «tecla pegada» ──────────────────────────────────────
    //       El dígito eliminado coincide con su vecino izquierdo o derecho,
    //       lo que indica una doble pulsación accidental de la misma tecla.
    const src = candidate.source;
    const idx = candidate.removedDigitIndex;
    const isKeyRepeat =
      (idx > 0 && src[idx] === src[idx - 1]) ||
      (idx < src.length - 1 && src[idx] === src[idx + 1]);
    if (isKeyRepeat) {
      score += 10;
      parts.push('tecla-pegada:+10');
    }

    return {
      ...candidate,
      score,
      reason: parts.join(', '),
    };
  });

  // Ordenar descendente por score; empates conservan el orden F2.06a (sort estable)
  ranked.sort((a, b) => b.score - a.score);

  return ranked;
}
